// Wires the document-kind lesson upload tab inside AddLessonModal.
// Flow per form:
//   1. User picks a file (input change or drop).
//   2. Browser POSTs filename / content_type / size to the sign
//      endpoint and gets back a key + presigned PUT URL.
//   3. Browser PUTs the file bytes directly to S3.
//   4. The hidden storage_key input gets the returned key; the
//      submit button enables; user clicks "Add lesson" and the
//      form POSTs normally to the existing AddLesson handler.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

export function initAcademyDocumentUpload(root = document) {
  const forms = root.querySelectorAll("[data-academy-document-form]");
  forms.forEach((form) => {
    if (!firstBind(form)) return;
    wireForm(form);
  });
}

function wireForm(form) {
  const signEndpoint = form.getAttribute("data-academy-document-form");
  const fileInput = form.querySelector("[data-academy-document-file]");
  const dropzone = form.querySelector("[data-academy-document-dropzone]");
  const prompt = form.querySelector("[data-academy-document-prompt]");
  const status = form.querySelector("[data-academy-document-status]");
  const keyInput = form.querySelector("[data-academy-document-key]");
  const submit = form.querySelector("[data-academy-document-submit]");
  if (!fileInput || !dropzone || !prompt || !status || !keyInput || !submit) {
    return;
  }

  let uploading = false;

  const setStatus = (text, isError = false) => {
    status.textContent = text;
    status.classList.toggle("hidden", !text);
    status.classList.toggle("text-red-600", isError);
    status.classList.toggle("text-ink-500", !isError);
  };

  const reset = () => {
    keyInput.value = "";
    submit.disabled = true;
    prompt.textContent = "Choose a file or drag one here";
    setStatus("");
  };

  reset();

  ["dragenter", "dragover"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add("ring-2", "ring-brand-500/40");
    }),
  );
  ["dragleave", "drop"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.remove("ring-2", "ring-brand-500/40");
    }),
  );
  dropzone.addEventListener("drop", (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (file) handle(file);
  });
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) handle(file);
  });

  const handle = async (file) => {
    if (uploading) return;
    uploading = true;
    submit.disabled = true;
    keyInput.value = "";
    prompt.textContent = file.name;
    setStatus("Preparing upload…");

    try {
      const signed = await sign(signEndpoint, file);
      setStatus("Uploading…");
      await upload(signed.upload_url, file, signed.upload_headers);
      keyInput.value = signed.key;
      submit.disabled = false;
      setStatus("Ready to attach.");
    } catch (err) {
      console.error("academy.document upload failed", err);
      setStatus(err.message || "Upload failed. Please try again.", true);
      reset();
    } finally {
      uploading = false;
    }
  };
}

async function sign(endpoint, file) {
  const body = new URLSearchParams();
  body.set("filename", file.name);
  body.set("content_type", file.type || "application/octet-stream");
  body.set("file_size_bytes", String(file.size));

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const payload = await safeJSON(res);
    throw new Error(payload?.error || `Sign step failed (${res.status})`);
  }
  return res.json();
}

// uploadHeaders are the signed metadata headers the sign step returned;
// S3 rejects the PUT signature unless they're echoed back verbatim.
async function upload(url, file, uploadHeaders) {
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(uploadHeaders || {}),
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
}

async function safeJSON(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}
